// ============================================================================
// recurringScheduleDerivation — pure date math that converts a recurring
// occurrence pageKey into the (scheduleMode, start, end) triple the rest of
// the app already understands.
//
// Lives here (not in recurringChecklistService or checklistViewAdapter) so
// both of those can import it without creating a circular dependency. Pure
// functions only — no Firestore, no side effects.
// ============================================================================

import type { RecurringMode } from '../types/recurringChecklist'

const isoDate = (date: Date): string => {
  const y = date.getFullYear().toString().padStart(4, '0')
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

const startOfIsoWeek = (date: Date): Date => {
  const clone = new Date(date)
  const delta = (clone.getDay() + 6) % 7
  clone.setDate(clone.getDate() - delta)
  clone.setHours(0, 0, 0, 0)
  return clone
}

export type RecurringDerivedSchedule = {
  scheduleMode: 'DATED' | 'DURATION'
  scheduleStartDate: string
  scheduleEndDate: string
}

/** Derive the (mode, start, end) triple a recurring checklist presents for a
 *  given occurrence pageKey. Callers use this to prefill the read-only
 *  Dated/Duration section in the drawer AND to inject dates onto the
 *  projected item so downstream calendar / dashboard / dependency code
 *  treats the instance as if it carried its own schedule.
 *
 *  DAILY  pageKey "YYYY-MM-DD" → DATED, start=end=key
 *  WEEKLY pageKey "YYYY-Www"   → DURATION, start=Monday, end=Sunday
 */
export const deriveRecurringScheduleForOccurrence = (
  mode: RecurringMode,
  pageKey: string,
): RecurringDerivedSchedule => {
  if (mode === 'DAILY') {
    return { scheduleMode: 'DATED', scheduleStartDate: pageKey, scheduleEndDate: pageKey }
  }
  const [yearStr, weekPart] = pageKey.split('-W')
  const year = Number(yearStr)
  const week = Number(weekPart)
  if (!Number.isFinite(year) || !Number.isFinite(week)) {
    const fallback = isoDate(new Date())
    return { scheduleMode: 'DURATION', scheduleStartDate: fallback, scheduleEndDate: fallback }
  }
  const jan4 = new Date(year, 0, 4)
  const week1Monday = startOfIsoWeek(jan4)
  const monday = new Date(week1Monday)
  monday.setDate(monday.getDate() + (week - 1) * 7)
  const sunday = new Date(monday)
  sunday.setDate(sunday.getDate() + 6)
  return {
    scheduleMode: 'DURATION',
    scheduleStartDate: isoDate(monday),
    scheduleEndDate: isoDate(sunday),
  }
}
